type SupabasePageError = {
  message: string;
  details?: string | null;
  hint?: string | null;
};

type SupabasePage<T> = {
  data: T[] | null;
  error: SupabasePageError | null;
};

const DEFAULT_PAGE_SIZE = 1000;

/**
 * Collect every row from a range-based Supabase query. Hosted projects commonly
 * cap a single Data API response at 1,000 rows, so counts derived from an
 * unpaged `.select()` become incorrect as soon as an import crosses that limit.
 */
export async function fetchAllSupabaseRows<T>(
  fetchPage: (from: number, to: number) => PromiseLike<SupabasePage<T>>,
  pageSize = DEFAULT_PAGE_SIZE,
): Promise<T[]> {
  if (!Number.isInteger(pageSize) || pageSize <= 0) {
    throw new Error("pageSize must be a positive integer");
  }

  const rows: T[] = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await fetchPage(from, from + pageSize - 1);
    if (error) {
      const detail = error.hint || error.details;
      throw new Error(detail ? `${error.message}: ${detail}` : error.message);
    }

    const page = data ?? [];
    rows.push(...page);
    if (page.length < pageSize) break;
  }

  return rows;
}
